import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import moment from 'moment';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { FileUpload } from 'primereact/fileupload';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Toast } from 'primereact/toast';
import { FilterMatchMode } from 'primereact/api';
import { getBranches, getVoucherExpenses, getVouchers, createVoucher, updateVoucher, resolveUploadUrl } from '../utilities/api';
import useUser from '../hooks/useUser';
import { amountToWordsINR } from './amountToWordsINR';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import '../masters/CategoryMaster.css';

const STATUS_OPTIONS = [
    { label: 'Approved', value: 'approved' },
    { label: 'Closed', value: 'closed' }
];

function voucherRowId(row) {
    return row?.vocher_id ?? row?.voucher_id ?? row?.id;
}

function normalizeVoucherList(raw) {
    const data = raw?.vochers ?? raw?.vouchers ?? raw?.data ?? raw;
    return Array.isArray(data) ? data : [];
}

function formatVoucherDate(value) {
    if (!value) return '—';
    const m = moment(value, ['YYYY-MM-DD', moment.ISO_8601, 'DD-MM-YYYY', 'DD-MM-YY'], true);
    return m.isValid() ? m.format('DD-MM-YYYY') : String(value);
}

const VoucherList = () => {
    const toast = useRef(null);
    const { userRole } = useUser();
    const rolename = userRole || localStorage.getItem('role') || '';
    const isCEO = String(rolename).toUpperCase() === 'CEO';
    const userId = localStorage.getItem('userid') || '';

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [branchOptions, setBranchOptions] = useState([]);
    const [expenseOptions, setExpenseOptions] = useState([]);

    // ── Header filters ──────────────────────────────────────
    const [draftFromDate, setDraftFromDate] = useState(null);
    const [draftToDate, setDraftToDate] = useState(null);
    const [draftBranchFilter, setDraftBranchFilter] = useState('All');
    const [draftExpenseFilter, setDraftExpenseFilter] = useState('All');
    const [appliedFromDate, setAppliedFromDate] = useState(null);
    const [appliedToDate, setAppliedToDate] = useState(null);
    const [appliedBranchFilter, setAppliedBranchFilter] = useState('All');
    const [appliedExpenseFilter, setAppliedExpenseFilter] = useState('All');

    // ── Global search ────────────────────────────────────────
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });

    // ── Add dialog ───────────────────────────────────────────
    const addFileRef = useRef(null);
    const editFileRef = useRef(null);
    const [addOpen, setAddOpen] = useState(false);
    const [addSaving, setAddSaving] = useState(false);
    const [addBranchId, setAddBranchId] = useState(null);
    const [addExpaId, setAddExpaId] = useState(null);
    const [addVoucherDate, setAddVoucherDate] = useState(new Date());
    const [addVoucherTime, setAddVoucherTime] = useState(new Date());
    const [addCustomerName, setAddCustomerName] = useState('');
    const [addVoucherAmount, setAddVoucherAmount] = useState(null);
    const [addVoucherAmountWords, setAddVoucherAmountWords] = useState('');
    const [addDescription, setAddDescription] = useState('');
    const [addVoucherFile, setAddVoucherFile] = useState(null);

    // ── Edit dialog ──────────────────────────────────────────
    const [editOpen, setEditOpen] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [editSaving, setEditSaving] = useState(false);
    const [confirmEditOpen, setConfirmEditOpen] = useState(false);
    const [pendingEditRow, setPendingEditRow] = useState(null);
    const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
    const [branchId, setBranchId] = useState(null);
    const [expaId, setExpaId] = useState(null);
    const [voucherDate, setVoucherDate] = useState(new Date());
    const [voucherTime, setVoucherTime] = useState(new Date());
    const [customerName, setCustomerName] = useState('');
    const [voucherAmount, setVoucherAmount] = useState(null);
    const [voucherAmountWords, setVoucherAmountWords] = useState('');
    const [description, setDescription] = useState('');
    const [status, setStatus] = useState('processing');
    const [originalStatus, setOriginalStatus] = useState('processing');
    const [editVoucherFile, setEditVoucherFile] = useState(null);
    const [existingFileUrl, setExistingFileUrl] = useState('');
    const [filePreviewOpen, setFilePreviewOpen] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState('');

    // ── Data loading ─────────────────────────────────────────
    const loadBranchesExpenses = useCallback(async () => {
        const clientId = localStorage.getItem('clientid');
        try {
            const branches = await getBranches(clientId);
            const arr = Array.isArray(branches) ? branches : [];
            setBranchOptions(arr.map((b) => ({
                label: b.branch_name || b.branchname || b.name || String(b.branch_id),
                value: String(b.branch_id)
            })));
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load branches' });
        }
        try {
            const res = await getVoucherExpenses({ client_id: clientId });
            const raw = res?.expnses ?? res?.expenses ?? res?.data ?? res;
            const list = Array.isArray(raw) ? raw : [];
            setExpenseOptions(list.map((e) => ({
                label: e.expanses_name || e.expa_name || e.expense_name || e.name || String(e.expa_id),
                value: String(e.expa_id)
            })));
        } catch {
            setExpenseOptions([]);
        }
    }, []);

    const fetchVouchers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getVouchers({ userId });
            const list = normalizeVoucherList(res);
            setRows(list.map((r, i) => ({ ...r, voucher_id: voucherRowId(r) ?? `row-${i}` })));
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err.message || 'Could not load vouchers' });
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            await loadBranchesExpenses();
            if (cancelled) return;
            await fetchVouchers();
        })();
        return () => { cancelled = true; };
    }, [loadBranchesExpenses, fetchVouchers]);

    // ── Amount-in-words sync ─────────────────────────────────
    useEffect(() => { setAddVoucherAmountWords(amountToWordsINR(addVoucherAmount)); }, [addVoucherAmount]);
    useEffect(() => { setVoucherAmountWords(amountToWordsINR(voucherAmount)); }, [voucherAmount]);

    // ── Derived ──────────────────────────────────────────────
    const canAdd = useMemo(
        () => addBranchId && addExpaId && addVoucherDate && addVoucherTime && addCustomerName.trim() && addVoucherAmount != null && userId,
        [addBranchId, addExpaId, addVoucherDate, addVoucherTime, addCustomerName, addVoucherAmount, userId]
    );

    const canSaveEdit = useMemo(
        () => branchId && expaId && voucherDate && voucherTime && customerName.trim() && voucherAmount != null && userId,
        [branchId, expaId, voucherDate, voucherTime, customerName, voucherAmount, userId]
    );

    const branchNameById = useMemo(() => {
        const map = new Map();
        branchOptions.forEach((b) => map.set(String(b.value), b.label));
        return map;
    }, [branchOptions]);

    const expenseNameById = useMemo(() => {
        const map = new Map();
        expenseOptions.forEach((e) => map.set(String(e.value), e.label));
        return map;
    }, [expenseOptions]);

    const branchFilterOptions = useMemo(() => [
        { label: 'All Branches', value: 'All' },
        ...branchOptions.map((b) => ({ label: b.label, value: b.value }))
    ], [branchOptions]);

    const expenseFilterOptions = useMemo(() => [
        { label: 'All Expenses', value: 'All' },
        ...expenseOptions.map((e) => ({ label: e.label, value: e.value }))
    ], [expenseOptions]);

    const filteredByHeader = useMemo(() => {
        let out = Array.isArray(rows) ? rows : [];
        if (appliedBranchFilter && appliedBranchFilter !== 'All')
            out = out.filter((r) => String(r.branch_id) === String(appliedBranchFilter));
        if (appliedExpenseFilter && appliedExpenseFilter !== 'All')
            out = out.filter((r) => String(r.expa_id) === String(appliedExpenseFilter));
        const getRowDate = (r) => r.vocher_date || r.voucher_date;
        if (appliedFromDate) {
            const from = moment(appliedFromDate).format('YYYY-MM-DD');
            out = out.filter((r) => {
                const md = moment(getRowDate(r), ['YYYY-MM-DD', moment.ISO_8601], true);
                return md.isValid() ? md.format('YYYY-MM-DD') >= from : false;
            });
        }
        if (appliedToDate) {
            const to = moment(appliedToDate).format('YYYY-MM-DD');
            out = out.filter((r) => {
                const md = moment(getRowDate(r), ['YYYY-MM-DD', moment.ISO_8601], true);
                return md.isValid() ? md.format('YYYY-MM-DD') <= to : false;
            });
        }
        return out;
    }, [rows, appliedBranchFilter, appliedExpenseFilter, appliedFromDate, appliedToDate]);

    const sumAmount = (list) => (Array.isArray(list) ? list : []).reduce((acc, r) => {
        const n = parseFloat(String(r?.vocher_amount ?? r?.voucher_amount ?? '').trim());
        return acc + (Number.isFinite(n) ? n : 0);
    }, 0);

    // Rows actually visible in the table after header filters + global search
    // (kept in sync via DataTable's onValueChange, which fires with the filtered/sorted dataset)
    const [visibleRows, setVisibleRows] = useState(filteredByHeader);
    useEffect(() => { setVisibleRows(filteredByHeader); }, [filteredByHeader]);

    const visibleAmountTotal = useMemo(() => sumAmount(visibleRows), [visibleRows]);

    const footerGroup = (
        <ColumnGroup>
            <Row>
                <Column footer="Total" colSpan={5} footerStyle={{ textAlign: 'right', fontWeight: 700 }} />
                <Column footer={`₹${visibleAmountTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} footerStyle={{ fontWeight: 700 }} />
                <Column colSpan={3} />
                <Column frozen alignFrozen="right" />
            </Row>
        </ColumnGroup>
    );

    // ── Filter handlers ──────────────────────────────────────
    const handleSearch = () => {
        if (draftFromDate && draftToDate) {
            const from = moment(draftFromDate).format('YYYY-MM-DD');
            const to = moment(draftToDate).format('YYYY-MM-DD');
            if (moment(to).isBefore(moment(from))) {
                toast.current?.show({ severity: 'warn', summary: 'Invalid range', detail: 'To date must not be before From date.' });
                return;
            }
        }
        setAppliedFromDate(draftFromDate);
        setAppliedToDate(draftToDate);
        setAppliedBranchFilter(draftBranchFilter || 'All');
        setAppliedExpenseFilter(draftExpenseFilter || 'All');
    };

    const handleFilterReset = () => {
        setDraftFromDate(null); setDraftToDate(null);
        setDraftBranchFilter('All'); setDraftExpenseFilter('All');
        setAppliedFromDate(null); setAppliedToDate(null);
        setAppliedBranchFilter('All'); setAppliedExpenseFilter('All');
    };

    // ── Add voucher ──────────────────────────────────────────
    const resetAddForm = () => {
        setAddBranchId(null); setAddExpaId(null);
        setAddVoucherDate(new Date()); setAddVoucherTime(new Date());
        setAddCustomerName(''); setAddVoucherAmount(null);
        setAddVoucherAmountWords(''); setAddDescription('');
        setAddVoucherFile(null); addFileRef.current?.clear();
    };

    const handleAddSubmit = async () => {
        if (!canAdd) {
            toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please fill branch, expense, date, time, customer, and amount.' });
            return;
        }
        setAddSaving(true);
        try {
            const fd = new FormData();
            fd.append('vocher_date', moment(addVoucherDate).format('YYYY-MM-DD'));
            fd.append('vocher_time', moment(addVoucherTime).format('HH:mm:ss'));
            fd.append('customer_name', addCustomerName.trim());
            fd.append('vocher_amount', String(addVoucherAmount));
            fd.append('vocher_amount_words', addVoucherAmountWords || '');
            fd.append('vocher_discriptions', addDescription);
            fd.append('branch_id', addBranchId);
            fd.append('userId', userId);
            fd.append('status', 'processing');
            fd.append('expa_id', addExpaId);
            if (addVoucherFile) fd.append('vocher_file', addVoucherFile);

            const res = await createVoucher(fd);
            const ok = res?.status === true || res?.status === 'success' || res?.success === true;
            if (ok) {
                toast.current?.show({ severity: 'success', summary: 'Saved', detail: res?.message || 'Voucher created' });
                setAddOpen(false);
                resetAddForm();
                await fetchVouchers();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: res?.message || res?.error || 'Could not create voucher' });
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err.message || 'Request failed' });
        } finally {
            setAddSaving(false);
        }
    };

    // ── Edit voucher ─────────────────────────────────────────
    const doOpenEdit = (row) => {
        const id = voucherRowId(row);
        if (!id) { toast.current?.show({ severity: 'warn', summary: 'Error', detail: 'Voucher id missing' }); return; }
        setEditRow(row);
        setBranchId(row.branch_id != null ? String(row.branch_id) : null);
        setExpaId(row.expa_id != null ? String(row.expa_id) : null);
        const d = row.vocher_date || row.voucher_date;
        const md = d ? moment(d, ['YYYY-MM-DD', moment.ISO_8601], true) : null;
        setVoucherDate(md?.isValid() ? md.toDate() : new Date());
        const t = row.vocher_time || row.voucher_time;
        const mt = t ? moment(t, [moment.ISO_8601, 'HH:mm:ss', 'HH:mm'], true) : null;
        setVoucherTime(mt?.isValid() ? mt.toDate() : new Date());
        setCustomerName(row.customer_name || '');
        const amt = row.vocher_amount ?? row.voucher_amount;
        setVoucherAmount(amt != null && amt !== '' ? Number(amt) : null);
        setDescription(row.vocher_discriptions || row.voucher_description || '');
        const rawStatus = row.status != null && row.status !== '' ? String(row.status) : 'processing';
        setOriginalStatus(rawStatus);
        setStatus(rawStatus.toLowerCase());
        setEditVoucherFile(null);
        editFileRef.current?.clear();
        setExistingFileUrl(resolveUploadUrl(row.vocher_file_url || row.vocher_file || row.vocher_file_name || row.file_name || row.file_url || ''));
        setEditOpen(true);
    };

    const openEdit = (row) => { setPendingEditRow(row); setConfirmEditOpen(true); };
    const confirmEdit = () => { setConfirmEditOpen(false); const row = pendingEditRow; setPendingEditRow(null); if (row) doOpenEdit(row); };

    const performSaveEdit = async () => {
        if (!canSaveEdit) { toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please fill required fields.' }); return; }
        setEditSaving(true);
        try {
            const fd = new FormData();
            fd.append('vocher_id', String(voucherRowId(editRow)));
            fd.append('vocher_date', moment(voucherDate).format('YYYY-MM-DD'));
            fd.append('vocher_time', moment(voucherTime).format('HH:mm:ss'));
            fd.append('customer_name', customerName.trim());
            fd.append('vocher_amount', String(voucherAmount));
            fd.append('vocher_amount_words', voucherAmountWords || '');
            fd.append('vocher_discriptions', description);
            fd.append('branch_id', branchId);
            fd.append('userId', userId);
            fd.append('expa_id', expaId);
            fd.append('status', status);
            if (editVoucherFile) fd.append('vocher_file', editVoucherFile);
            const res = await updateVoucher(fd);
            const ok = res?.status === true || res?.status === 'success' || res?.success === true;
            if (ok) {
                toast.current?.show({ severity: 'success', summary: 'Updated', detail: res?.message || 'Voucher updated' });
                setEditOpen(false);
                await fetchVouchers();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: res?.message || res?.error || 'Update failed' });
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err.message || 'Request failed' });
        } finally {
            setEditSaving(false);
        }
    };

    const editStatusOptions = useMemo(() => {
        const v = (status || '').toLowerCase();
        if (!v || STATUS_OPTIONS.some((o) => o.value === v)) return STATUS_OPTIONS;
        return [{ label: status, value: v }, ...STATUS_OPTIONS];
    }, [status]);

    // ── Export ───────────────────────────────────────────────
    const exportBaseRows = useMemo(() => filteredByHeader.map((v, i) => ({
        sl_no: i + 1,
        vocher_no: v.vocher_no || v.voc_no || '',
        vocher_date: formatVoucherDate(v.vocher_date || v.voucher_date),
        vocher_time: v.vocher_time || v.voucher_time || '',
        customer_name: v.customer_name || '',
        vocher_amount: v.vocher_amount ?? v.voucher_amount ?? 0,
        expense_name: expenseNameById.get(String(v.expa_id)) || v.expanses_name || v.expa_id || '',
        branch_name: branchNameById.get(String(v.branch_id)) || v.branch_name || v.branch_id || '',
        status: v.status || ''
    })), [filteredByHeader, expenseNameById, branchNameById]);

    const exportToExcel = () => {
        const data = exportBaseRows.map((r) => ({ 'Sl. No': r.sl_no, 'Voucher No': r.vocher_no, Date: r.vocher_date, Time: r.vocher_time, Customer: r.customer_name, Amount: r.vocher_amount, Expense: r.expense_name, Branch: r.branch_name, Status: r.status }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Vouchers');
        saveAs(new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: 'application/octet-stream' }), `Vouchers_${moment().format('YYYY-MM-DD')}.xlsx`);
    };

    const exportToPDF = () => {
        if (!exportBaseRows.length) return;
        const doc = new jsPDF();
        doc.setFontSize(14);
        doc.text('Vouchers', 14, 15);
        autoTable(doc, {
            startY: 22,
            head: [['Sl. No', 'Voucher No', 'Date', 'Time', 'Customer', 'Amount', 'Expense', 'Branch', 'Status']],
            body: exportBaseRows.map((r) => [r.sl_no, r.vocher_no || '-', r.vocher_date || '-', r.vocher_time || '-', r.customer_name || '-', r.vocher_amount || '0', r.expense_name || '-', r.branch_name || '-', r.status || '-']),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [111, 66, 193] }
        });
        doc.save(`Vouchers_${moment().format('YYYY-MM-DD')}.pdf`);
    };

    // ── Column templates ─────────────────────────────────────
    const statusBody = (row) => {
        const s = (row.status || '').toLowerCase();
        return <Tag value={row.status || '—'} severity={s === 'approved' ? 'success' : s === 'processing' ? 'warning' : 'info'} rounded style={{ minWidth: '80px', textAlign: 'center' }} />;
    };

    const actionBody = (row) => (
        <Button icon="pi pi-pencil" rounded text className="p-button-warning" onClick={() => openEdit(row)} tooltip="Edit" tooltipOptions={{ position: 'top' }} />
    );

    // ── Render ───────────────────────────────────────────────
    return (
        <div className="masters-page-outer">
            <Toast ref={toast} />

            <div className="masters-page-header">
                <h2>Voucher List</h2>
                <p>View, filter and manage cash vouchers</p>
            </div>

            {/* Filter card */}
            <Card className="mb-3 bill-reports-filter-card">
                <div className="flex flex-wrap gap-3 align-items-end bill-reports-filter-row">
                    <div className="flex flex-column" style={{ flex: '1 1 160px', minWidth: '160px' }}>
                        <label className="mb-2 font-medium text-sm">From Date</label>
                        <Calendar value={draftFromDate} onChange={(e) => setDraftFromDate(e.value)} showIcon dateFormat="dd-mm-yy" placeholder="From date" className="w-full" showButtonBar />
                    </div>
                    <div className="flex flex-column" style={{ flex: '1 1 160px', minWidth: '160px' }}>
                        <label className="mb-2 font-medium text-sm">To Date</label>
                        <Calendar value={draftToDate} onChange={(e) => setDraftToDate(e.value)} showIcon dateFormat="dd-mm-yy" placeholder="To date" className="w-full" showButtonBar minDate={draftFromDate || undefined} />
                    </div>
                    <div className="flex flex-column" style={{ flex: '1 1 180px', minWidth: '180px' }}>
                        <label className="mb-2 font-medium text-sm">Branch</label>
                        <Dropdown value={draftBranchFilter} options={branchFilterOptions} onChange={(e) => setDraftBranchFilter(e.value)} placeholder="All Branches" className="w-full" filter showClear={false} />
                    </div>
                    <div className="flex flex-column" style={{ flex: '1 1 180px', minWidth: '180px' }}>
                        <label className="mb-2 font-medium text-sm">Expense</label>
                        <Dropdown value={draftExpenseFilter} options={expenseFilterOptions} onChange={(e) => setDraftExpenseFilter(e.value)} placeholder="All Expenses" className="w-full" filter showClear={false} />
                    </div>
                    <div className="flex gap-2" style={{ flex: '0 0 auto' }}>
                        <Button label="Search" icon="pi pi-search" className="p-button-primary" onClick={handleSearch} />
                        <Button label="Reset" icon="pi pi-refresh" className="p-button-secondary" onClick={handleFilterReset} />
                    </div>
                </div>
            </Card>

            {/* Data card */}
            <Card className="bill-reports-container">
                <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                    <div className="flex align-items-center gap-2 flex-wrap">
                        <h3 className="text-xl font-semibold m-0">Vouchers</h3>
                        {!loading && (
                            <span style={{ fontSize: '0.82rem', color: '#6b7280', background: '#f3f4f6', borderRadius: '12px', padding: '0.2rem 0.7rem', fontWeight: 600 }}>
                                {visibleRows.length} record{visibleRows.length !== 1 ? 's' : ''}&nbsp;|&nbsp;Total: ₹{visibleAmountTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2 align-items-center">
                        <span className="p-input-icon-left" style={{ minWidth: '200px' }}>
                            <i className="pi pi-search" />
                            <InputText value={globalFilterValue} onChange={(e) => { setGlobalFilterValue(e.target.value); setFilters({ global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } }); }} placeholder="Search vouchers..." className="w-full" />
                        </span>
                        <Button icon="pi pi-file-excel" className="masters-excel-btn" onClick={exportToExcel} disabled={!filteredByHeader.length} tooltip="Export Excel" tooltipOptions={{ position: 'top' }} />
                        <Button icon="pi pi-file-pdf" className="p-button-danger p-button-sm" onClick={exportToPDF} disabled={!filteredByHeader.length} tooltip="Export PDF" tooltipOptions={{ position: 'top' }} />
                        <Button label="Add Voucher" icon="pi pi-plus" className="p-button-primary" onClick={() => { resetAddForm(); setAddOpen(true); }} />
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <ProgressSpinner />
                    </div>
                ) : (
                    <div className="sub-masters-table-wrap">
                        <DataTable
                            value={filteredByHeader}
                            filters={filters}
                            globalFilterFields={['customer_name', 'vocher_no', 'status']}
                            onValueChange={setVisibleRows}
                            footerColumnGroup={footerGroup}
                            paginator rows={10} rowsPerPageOptions={[10, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                            showGridlines tableStyle={{ minWidth: '50rem' }}
                            className="p-datatable-sm sub-masters-table"
                            dataKey="voucher_id" emptyMessage="No vouchers found"
                        >
                            <Column header="Sl. No" body={(_, opt) => (opt?.rowIndex ?? 0) + 1} style={{ width: '70px' }} />
                            <Column
                                field="vocher_no"
                                header="Voucher No"
                                sortable
                                body={(r) => {
                                    const imgUrl = resolveUploadUrl(r.vocher_file_url || r.vocher_file || r.vocher_file_name || r.file_name || r.file_url || '');
                                    return (
                                        <button
                                            type="button"
                                            onClick={() => { if (imgUrl) { setPreviewImageUrl(imgUrl); setFilePreviewOpen(true); } }}
                                            style={{ background: 'none', border: 'none', padding: 0, color: imgUrl ? '#6f42c1' : '#374151', fontWeight: 600, cursor: imgUrl ? 'pointer' : 'default', textDecoration: imgUrl ? 'underline' : 'none', fontSize: 'inherit' }}
                                            title={imgUrl ? 'Click to view image' : ''}
                                        >
                                            {r.vocher_no || r.voc_no || '—'}
                                        </button>
                                    );
                                }}
                            />
                            <Column field="vocher_date" header="Date" body={(r) => formatVoucherDate(r.vocher_date || r.voucher_date)} sortable style={{ width: '120px' }} />
                            <Column field="vocher_time" header="Time" body={(r) => r.vocher_time || r.voucher_time || '—'} style={{ width: '100px' }} />
                            <Column field="customer_name" header="Customer" sortable />
                            <Column field="vocher_amount" header="Amount (₹)" body={(r) => r.vocher_amount ?? r.voucher_amount ?? '—'} sortable style={{ width: '120px' }} />
                            <Column header="Expense" body={(r) => expenseNameById.get(String(r.expa_id)) || r.expanses_name || r.expa_id || '—'} />
                            <Column header="Branch" body={(r) => branchNameById.get(String(r.branch_id)) || r.branch_name || r.branch_id || '—'} />
                            <Column field="status" header="Status" body={statusBody} style={{ width: '120px', textAlign: 'center' }} />
                            <Column header="Actions" body={actionBody} style={{ width: '90px', textAlign: 'center' }} frozen alignFrozen="right" />
                        </DataTable>
                    </div>
                )}
            </Card>

            {/* ── Add Voucher Dialog ─────────────────────────── */}
            <Dialog
                header={<div className="sub-dialog-header"><span className="sub-dialog-header-icon"><i className="pi pi-plus" /></span><span className="sub-dialog-header-text">Add Voucher</span></div>}
                visible={addOpen}
                style={{ width: 'min(680px, 96vw)' }}
                onHide={() => { setAddOpen(false); resetAddForm(); }}
                className="sub-master-dialog"
                footer={
                    <div className="flex gap-2 justify-content-end">
                        <Button label="Clear" icon="pi pi-refresh" className="sub-dialog-cancel" onClick={resetAddForm} disabled={addSaving} />
                        <Button label="Submit" icon="pi pi-check" className="sub-dialog-save" onClick={handleAddSubmit} loading={addSaving} disabled={!canAdd} />
                    </div>
                }
            >
                <div className="p-fluid formgrid grid">
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Branch</label>
                        <Dropdown value={addBranchId} options={branchOptions} onChange={(e) => setAddBranchId(e.value)} placeholder="Select branch" filter showClear />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Expense</label>
                        <Dropdown value={addExpaId} options={expenseOptions} onChange={(e) => setAddExpaId(e.value)} placeholder="Select expense" filter showClear />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Voucher Date</label>
                        <Calendar value={addVoucherDate} onChange={(e) => setAddVoucherDate(e.value)} showIcon dateFormat="dd-mm-yy" />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Voucher Time</label>
                        <Calendar value={addVoucherTime} onChange={(e) => setAddVoucherTime(e.value)} timeOnly hourFormat="24" showIcon />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Customer Name</label>
                        <InputText value={addCustomerName} onChange={(e) => setAddCustomerName(e.target.value)} placeholder="Enter customer name" />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Voucher Amount</label>
                        <InputNumber value={addVoucherAmount} onValueChange={(e) => setAddVoucherAmount(e.value)} mode="decimal" minFractionDigits={0} maxFractionDigits={2} useGrouping={false} placeholder="0.00" />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Amount in Words</label>
                        <InputTextarea value={addVoucherAmountWords} readOnly rows={2} className="w-full" />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Attachment <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.8rem' }}>(optional)</span></label>
                        <div className="flex align-items-center gap-2 flex-wrap">
                            <FileUpload ref={addFileRef} mode="basic" name="vocher_file" accept="image/*" maxFileSize={10000000} chooseLabel={addVoucherFile ? addVoucherFile.name : 'Choose file'} onSelect={(e) => setAddVoucherFile(e.files?.[0] || null)} className="p-button-sm" auto={false} />
                            {addVoucherFile && <Button type="button" label="Clear" icon="pi pi-times" className="p-button-text p-button-sm p-button-danger" onClick={() => { setAddVoucherFile(null); addFileRef.current?.clear(); }} />}
                        </div>
                        {addVoucherFile && <small className="text-color-secondary mt-1 block">{addVoucherFile.name} ({(addVoucherFile.size / 1024).toFixed(1)} KB)</small>}
                    </div>
                    <div className="field col-12">
                        <label className="font-medium">Description</label>
                        <InputTextarea value={addDescription} onChange={(e) => setAddDescription(e.target.value)} rows={3} className="w-full" placeholder="Notes or description" />
                    </div>
                </div>
            </Dialog>

            {/* ── Edit Voucher Dialog ────────────────────────── */}
            <Dialog
                header={<div className="sub-dialog-header"><span className="sub-dialog-header-icon"><i className="pi pi-pencil" /></span><span className="sub-dialog-header-text">Edit Voucher</span></div>}
                visible={editOpen}
                style={{ width: 'min(640px, 96vw)' }}
                onHide={() => { setEditOpen(false); setEditVoucherFile(null); setExistingFileUrl(''); editFileRef.current?.clear(); }}
                className="sub-master-dialog"
                footer={
                    <div className="flex gap-2 justify-content-end">
                        <Button label="Cancel" className="sub-dialog-cancel" onClick={() => { setEditOpen(false); setEditVoucherFile(null); setExistingFileUrl(''); editFileRef.current?.clear(); }} />
                        <Button label="Save" icon="pi pi-check" className="sub-dialog-save" onClick={() => setConfirmSaveOpen(true)} loading={editSaving} disabled={!canSaveEdit} />
                    </div>
                }
            >
                <div className="p-fluid formgrid grid">
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Branch</label>
                        <Dropdown value={branchId} options={branchOptions} onChange={(e) => setBranchId(e.value)} placeholder="Select branch" filter showClear />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Expense</label>
                        <Dropdown value={expaId} options={expenseOptions} onChange={(e) => setExpaId(e.value)} placeholder="Select expense" filter showClear />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Voucher Date</label>
                        <Calendar value={voucherDate} onChange={(e) => setVoucherDate(e.value)} showIcon dateFormat="dd-mm-yy" />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Voucher Time</label>
                        <Calendar value={voucherTime} onChange={(e) => setVoucherTime(e.value)} timeOnly hourFormat="24" showIcon />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Customer Name</label>
                        <InputText value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Voucher Amount</label>
                        <InputNumber value={voucherAmount} onValueChange={(e) => setVoucherAmount(e.value)} mode="decimal" minFractionDigits={0} maxFractionDigits={2} useGrouping={false} />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Amount in Words</label>
                        <InputTextarea value={voucherAmountWords} readOnly rows={2} className="w-full" />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Status</label>
                        <Dropdown value={status} options={editStatusOptions} onChange={(e) => setStatus(e.value)} placeholder="Select status" disabled={!isCEO} />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Attachment <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.8rem' }}>(optional)</span></label>

                        {/* Previous file */}
                        {existingFileUrl && (
                            <div className="mb-2" style={{ padding: '0.55rem 0.75rem', background: '#f8f4ff', borderRadius: '8px', border: '1px solid #e2d5f0' }}>
                                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.35rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Current file
                                </div>
                                {/\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(existingFileUrl) ? (
                                    <img
                                        src={existingFileUrl}
                                        alt="attachment"
                                        onClick={() => setFilePreviewOpen(true)}
                                        style={{ maxHeight: '90px', maxWidth: '100%', borderRadius: '6px', border: '1px solid #d8c8f0', display: 'block', cursor: 'pointer' }}
                                        title="Click to preview"
                                    />
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => setFilePreviewOpen(true)}
                                        style={{ background: 'none', border: 'none', padding: 0, color: '#6f42c1', fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                                    >
                                        <i className="pi pi-file" /> View file
                                    </button>
                                )}
                                {editVoucherFile && (
                                    <small style={{ color: '#f59e0b', display: 'block', marginTop: '0.3rem' }}>
                                        <i className="pi pi-exclamation-triangle" /> Will be replaced by new file
                                    </small>
                                )}
                            </div>
                        )}

                        {/* New file upload */}
                        <div className="flex align-items-center gap-2 flex-wrap">
                            <FileUpload
                                ref={editFileRef}
                                mode="basic"
                                name="vocher_file"
                                accept="image/*"
                                maxFileSize={10000000}
                                chooseLabel={editVoucherFile ? editVoucherFile.name : existingFileUrl ? 'Replace file' : 'Choose file'}
                                onSelect={(e) => setEditVoucherFile(e.files?.[0] || null)}
                                className="p-button-sm"
                                auto={false}
                            />
                            {editVoucherFile && (
                                <Button type="button" label="Clear" icon="pi pi-times" className="p-button-text p-button-sm p-button-danger"
                                    onClick={() => { setEditVoucherFile(null); editFileRef.current?.clear(); }} />
                            )}
                        </div>
                        {editVoucherFile && (
                            <small className="text-color-secondary mt-1 block">{editVoucherFile.name} ({(editVoucherFile.size / 1024).toFixed(1)} KB)</small>
                        )}
                    </div>
                    <div className="field col-12">
                        <label className="font-medium">Description</label>
                        <InputTextarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full" />
                    </div>
                </div>
            </Dialog>

            {/* Confirm edit open */}
            <Dialog header="Confirm" visible={confirmEditOpen} modal closable={false} style={{ width: 'min(400px, 96vw)' }} onHide={() => setConfirmEditOpen(false)}
                footer={<div className="flex gap-2 justify-content-end">
                    <Button label="Cancel" className="sub-dialog-cancel" onClick={() => setConfirmEditOpen(false)} />
                    <Button label="Yes, Edit" icon="pi pi-pencil" className="sub-dialog-save" onClick={confirmEdit} />
                </div>}
            >
                <div>Do you want to edit this voucher? <span className="font-medium">{pendingEditRow?.vocher_no || ''}</span></div>
            </Dialog>

            {/* Confirm save */}
            <Dialog header="Confirm" visible={confirmSaveOpen} modal closable={false} style={{ width: 'min(400px, 96vw)' }} onHide={() => setConfirmSaveOpen(false)}
                footer={<div className="flex gap-2 justify-content-end">
                    <Button label="Cancel" className="sub-dialog-cancel" onClick={() => setConfirmSaveOpen(false)} />
                    <Button label="Yes, Save" icon="pi pi-check" className="sub-dialog-save" onClick={async () => { setConfirmSaveOpen(false); await performSaveEdit(); }} loading={editSaving} />
                </div>}
            >
                <div>Do you want to save the changes?</div>
            </Dialog>

            {/* ── Image Preview Dialog (Voucher No click + edit current file) ── */}
            <Dialog
                header={
                    <div className="sub-dialog-header">
                        <span className="sub-dialog-header-icon"><i className="pi pi-image" /></span>
                        <span className="sub-dialog-header-text">Image Preview</span>
                    </div>
                }
                visible={filePreviewOpen}
                style={{ width: 'min(720px, 96vw)' }}
                onHide={() => { setFilePreviewOpen(false); setPreviewImageUrl(''); }}
                className="sub-master-dialog"
                footer={
                    <div className="flex justify-content-end">
                        <Button label="Close" icon="pi pi-times" className="sub-dialog-cancel" onClick={() => { setFilePreviewOpen(false); setPreviewImageUrl(''); }} />
                    </div>
                }
            >
                {(previewImageUrl || existingFileUrl) && (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                        <img
                            src={previewImageUrl || existingFileUrl}
                            alt="voucher attachment"
                            style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '8px', objectFit: 'contain' }}
                        />
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default VoucherList;
